import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  InputAdornment,
  Typography,
  MenuItem,
  TablePagination,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Search,
  Add,
  Refresh,
  DirectionsCar,
  OpenInNew,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import api from "../../api/axiosInstance";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import VehicleFormDialog from "../../components/vehicles/VehicleFormDialog";
import { selectStatusOptions, selectStatusChipConfig } from "../../redux/slices/statusDefinitionsSlice";
import { formatUsd } from "../../utils/currency";
import useUrlState from "../../hooks/useUrlState";
import useScrollRestoration from "../../hooks/useScrollRestoration";

const URL_SCHEMA = {
  search: { default: "" },
  statusFilter: { default: "" },
  page: { default: 0, parse: (v) => parseInt(v, 10) || 0 },
  rowsPerPage: { default: 20, parse: (v) => parseInt(v, 10) || 20 },
};

const AdminVehicles = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const vehicleStatusOptions = useSelector(selectStatusOptions("vehicle"));
  const statusChipConfig = useSelector(selectStatusChipConfig);
  const STATUSES = [{ value: "", label: "All Statuses" }, ...vehicleStatusOptions];

  useScrollRestoration();
  const [urlState, setUrlState] = useUrlState(URL_SCHEMA);
  const { statusFilter, page, rowsPerPage } = urlState;
  // Local typing buffer — committed to the URL (and re-queried) only on
  // Enter, same as before.
  const [search, setSearch] = useState(urlState.search);
  const [vehicles, setVehicles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(
    async (overrides = {}) => {
      setLoading(true);
      try {
        const { data } = await api.get("/vehicles", {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            status: statusFilter || undefined,
            search: search || undefined,
            ...overrides,
          },
        });
        setVehicles(data.data || []);
        setTotal(data.pagination?.total || 0);
      } catch {
        enqueueSnackbar("Failed to load vehicle shipments", {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, statusFilter, search, enqueueSnackbar],
  );

  useEffect(() => {
    load();
  }, [page, rowsPerPage, statusFilter]);

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setUrlState({ search: e.target.value, page: 0 });
      load({ page: 1, search: e.target.value });
    }
  };

  const handleCreate = async (payload) => {
    setCreating(true);
    try {
      const { data } = await api.post("/vehicles", payload);
      enqueueSnackbar("Vehicle shipment created", { variant: "success" });
      setCreateOpen(false);
      navigate(`/admin/vehicles/${data.data.vehicle.trackingId}`);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to create", {
        variant: "error",
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Vehicle Imports"
        subtitle={`${total} vehicle shipments`}
        action={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateOpen(true)}
          >
            New Vehicle Shipment
          </Button>
        }
      />

      {/* Summary chips — one per status currently present on this page, driven entirely by DB data */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {vehicleStatusOptions.map((s) => {
          const count = vehicles.filter((v) => v.status === s.value).length;
          if (!count) return null;
          const cfg = statusChipConfig[s.value] || { bg: "#EDF2F7", color: "#4A5568" };
          return (
            <Chip
              key={s.value}
              label={`${s.label}: ${count}`}
              size="small"
              onClick={() => setUrlState({ statusFilter: s.value, page: 0 })}
              sx={{
                cursor: "pointer",
                bgcolor: cfg.bg,
                color: cfg.color,
                fontWeight: 600,
                fontSize: 11,
              }}
            />
          );
        })}
      </Box>

      <Card>
        {/* Toolbar */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Search tracking ID, customer, make…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearch}
            sx={{ width: 300 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 16, color: "text.disabled" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e) => setUrlState({ statusFilter: e.target.value, page: 0 })}
            sx={{ width: 200 }}
          >
            {STATUSES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => load()}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {loading && <LinearProgress />}

        {!loading && vehicles.length === 0 ? (
          <EmptyState
            icon="🚗"
            title="No vehicle shipments found"
            description="Create a new vehicle shipment to start tracking imports."
            actionLabel="New Vehicle Shipment"
            onAction={() => setCreateOpen(true)}
          />
        ) : (
          <>
            <Box sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Tracking ID</TableCell>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Origin</TableCell>
                    <TableCell>Vessel</TableCell>
                    <TableCell>ETA</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vehicles.map((v) => (
                    <TableRow
                      key={v._id}
                      hover
                      onClick={() => navigate(`/admin/vehicles/${v.trackingId}`)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          fontSize: 12,
                          fontWeight: 700,
                          color: "primary.main",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {v.trackingId}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <DirectionsCar
                            sx={{ fontSize: 16, color: "text.disabled" }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight={700}>
                              {v.vehicle?.year} {v.vehicle?.make}{" "}
                              {v.vehicle?.model}
                            </Typography>
                            {v.vehicle?.color && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {v.vehicle.color}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {v.customerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {v.customerPhone}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                        {v.origin?.country}
                        {v.origin?.port ? ` · ${v.origin.port}` : ""}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, color: "text.secondary" }}>
                        {v.shipping?.vesselName || "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: 12,
                          whiteSpace: "nowrap",
                          color: "text.secondary",
                        }}
                      >
                        {v.shipping?.estimatedArrivalDate
                          ? dayjs(v.shipping.estimatedArrivalDate).format(
                            "MMM D, YYYY",
                          )
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={v.statusLabel || v.status}
                          size="small"
                          sx={{
                            bgcolor: (statusChipConfig[v.status] || {}).bg || "#EDF2F7",
                            color: (statusChipConfig[v.status] || {}).color || "#4A5568",
                            fontWeight: 600,
                            fontSize: "0.6875rem",
                            height: 22,
                            "& .MuiChip-label": { px: 1.25 },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        {v.declaredValue ? formatUsd(v.declaredValue) : "—"}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Open detail">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/vehicles/${v.trackingId}`);
                            }}
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setUrlState({ page: p })}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => setUrlState({ rowsPerPage: +e.target.value, page: 0 })}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{ borderTop: "1px solid", borderColor: "divider" }}
            />
          </>
        )}
      </Card>

      <VehicleFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        loading={creating}
      />
    </Box>
  );
};

export default AdminVehicles;
